import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Button, Textarea, Text, Tooltip, Image } from '@fluentui/react-components';
import { CommentEdit16Regular, Dismiss16Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { UserPicture } from '../../Common/UserPicture';
import { LexicalEditor, LexicalEditorRef } from '../../Common/LexicalEdit/LexicalEditor';
import './QueryMessage.scss';
import { useConversations } from '../../../store/hooks';
import { performEnterpriseGPTApiCall } from '../../../Hooks/useMiddlewareApiCall';
import { AttachmentExtensionType } from '../../../EnterpriseGPTApi/EnterpriseGPTApiV1Client';
import MarkdownPresenter from '../../Common/MarkdownPresenter';

// manages the query message
export interface QueryMessageProps {
    id?: string;
    text: string;
    onUpdate: (text: string) => void;
}
export interface ImageReference {
    url: string;
    fileName: string;
}
export const QueryMessage: FC<QueryMessageProps> = (props: QueryMessageProps) => {
    const { id, text, onUpdate } = props;
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [editedText, setEditedText] = useState<string>(text);
    const { t } = useTranslation();
    const { activeChat, activeGpt } = useConversations();
    const [imageUrls, setImageUrls] = useState<ImageReference[]>([]);

    useEffect(() => {
        setEditedText(text);
    }, [text]);

    // Ref for LexicalEditor
    const editorRef = useRef<LexicalEditorRef>(null);

    // Clear editor content when exiting edit mode
    useEffect(() => {
        if (!isEditing && editorRef.current) {
            editorRef.current.clearContent();
        }
    }, [isEditing]);

    // Set editor content only when entering edit mode for the first time or when text changes
    useEffect(() => {
        if (isEditing && editorRef.current) {
            editorRef.current.setContent(text);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEditing, text]);

    // Handle Enter key to save edit
    // Use native KeyboardEvent for compatibility with LexicalEditor
    const handleKeyDown = useCallback((event: KeyboardEvent) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            setIsEditing(false);
            onUpdate(editedText);
            setEditedText('');
        }
    }, [editedText, onUpdate]);

    // if the user has uploaded an image, show the image preview in the query message
    useEffect(() => {
        const firstPrompt = activeChat?.prompts?.at(0);
        if (firstPrompt?.id === props.id && activeChat?.attachments?.some((a) => a.extensionType === AttachmentExtensionType.Image)) {
            const imageAttachments = (activeChat.attachments || []).filter((a) => a.extensionType === AttachmentExtensionType.Image);
            setImageUrls(imageAttachments.map((a) => ({ url: '', fileName: a.name || '' })));
            imageAttachments.forEach((attachment) => {
                performEnterpriseGPTApiCall(async (client) => {
                    if (activeChat && attachment?.name && attachment.extensionType === AttachmentExtensionType.Image) {
                        const file = await client.downloadAttachment(activeChat.id, attachment.name);
                        const url = URL.createObjectURL(file.data);
                        setImageUrls((imageUrls) => imageUrls.map((i) => (i.fileName === attachment.name ? { ...i, url } : i)));
                    }
                });
            });
        }
    }, [activeChat, props.id]);

    return (
        <div className='queryMessage'>
            <div className='inner'>
                <UserPicture />
                <div className='content'>
                    {isEditing ? (
                        <>
                        
                            <LexicalEditor
                                ref={editorRef}
                                onChange={setEditedText}
                                onKeyDown={handleKeyDown}
                                placeholder={t('query.editPlaceholder') || 'Edit your message...'}
                                autoFocus={true}
                                className='lexicaleditor'
                            />


                            <div className='edit'>
                                <Button
                                    appearance='primary'
                                    onClick={() => {
                                        setIsEditing(false);
                                        onUpdate(editedText);
                                        setEditedText(''); // Clear edited text after update
                                    }}
                                >
                                    {t('query.saveSubmit')}
                                </Button>
                                <Button
                                    appearance='secondary'
                                    onClick={() => {
                                        setEditedText(text);
                                        setIsEditing(false);
                                    }}
                                >
                                    {t('query.cancel')}
                                </Button>
                            </div>
                        </>
                    ) : (
                        <>                            
                            <div className='queryMessageText'>
                                <MarkdownPresenter text={text} />
                            </div>
                            {imageUrls &&
                                imageUrls.map((imageUrl, index) => (
                                    // set the image preview to a fixed width
                                    <Image key={`img_${id}_${index}`} src={imageUrl.url} width={300} />
                                ))}
                        </>
                    )}
                </div>
                <div className='actions'>
                    <Tooltip content={isEditing ? 'Cancel' : 'Edit query'} relationship='label'>
                        <Button
                            size='medium'
                            icon={isEditing ? <Dismiss16Regular /> : <CommentEdit16Regular />}
                            appearance='transparent'
                            shape='circular'
                            onClick={() => {
                                setIsEditing(!isEditing);
                            }}
                        />
                    </Tooltip>
                </div>
            </div>
        </div>
    );
};
